import React from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAdminAuth } from '@/contexts/AdminAuthContext'
import { Button } from '@/components/ui/button'
import { 
  Crown, 
  Users, 
  CreditCard, 
  BarChart3, 
  Settings, 
  HeadphonesIcon, 
  Monitor,
  LogOut,
  Mail,
  Home,
  Palette
} from 'lucide-react'

export default function AdminLayout() {
  const { user, logout } = useAdminAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const handleLogout = () => {
    logout()
    navigate('/admin')
  }

  const navigation = [
    {
      name: 'Dashboard',
      href: '/admin/dashboard',
      icon: Home,
      description: 'System overview and metrics'
    },
    {
      name: 'Users',
      href: '/admin/users',
      icon: Users,
      description: 'User management and analytics'
    },
    {
      name: 'Plans',
      href: '/admin/plans',
      icon: CreditCard,
      description: 'Create and manage subscription plans'
    },
    {
      name: 'Analytics',
      href: '/admin/analytics',
      icon: BarChart3,
      description: 'Advanced reporting and insights'
    },
    {
      name: 'Support',
      href: '/admin/support',
      icon: HeadphonesIcon,
      description: 'Support tickets and user assistance'
    },
    {
      name: 'System',
      href: '/admin/system',
      icon: Monitor,
      description: 'System monitoring and logs'
    },
    {
      name: 'Settings',
      href: '/admin/settings',
      icon: Settings,
      description: 'Application configuration'
    },
    {
      name: 'Branding',
      href: '/admin/branding',
      icon: Palette,
      description: 'Application branding and appearance'
    }
  ]

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-80 bg-white border-r border-gray-200 shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-center h-16 px-6 border-b border-gray-100 bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="flex items-center space-x-3">
            <Crown className="h-8 w-8 text-white" />
            <div className="text-white">
              <div className="text-lg font-bold">Super Admin</div>
              <div className="text-xs opacity-90">redgeGate</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon
            const active = isActive(item.href)
            
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={`group flex items-start px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  active
                    ? 'bg-gradient-to-r from-purple-50 to-blue-50 text-purple-700 border-l-2 border-purple-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon
                  className={`mr-3 h-5 w-5 transition-colors ${
                    active ? 'text-purple-600' : 'text-gray-400 group-hover:text-gray-600'
                  }`}
                />
                <div className="flex-1">
                  <div className="font-medium">{item.name}</div>
                  <div className={`text-xs mt-0.5 ${
                    active ? 'text-purple-600' : 'text-gray-500'
                  }`}>
                    {item.description}
                  </div>
                </div>
              </NavLink>
            )
          })}
        </nav>

        {/* User Profile & Logout */}
        <div className="border-t border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center">
                <Crown className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500 flex items-center">
                  <Mail className="h-3 w-3 mr-1" />
                  {user?.email}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-gray-500 hover:text-red-600"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-80">
        <div className="min-h-screen">
          <Outlet />
        </div>
      </div>
    </div>
  )
}